import React from 'react';
import { HelpCircle } from 'lucide-react';
import { ValuationData, ValidationErrors } from '../../../types/valuation';
import { Tooltip } from '../../ui/Tooltip';
import clsx from 'clsx';

interface OperationalMetricsProps {
  data: ValuationData;
  onUpdate: (data: Partial<ValuationData>) => void;
  errors?: ValidationErrors;
}

const fuelTypes = [
  'Regular Unleaded',
  'Premium Unleaded',
  'Diesel',
  'Electric Charging',
  'E85 (Flex Fuel)',
  'Bio-Diesel',
  'CNG (Compressed Natural Gas)',
  'LPG (Propane)',
  'Hydrogen'
];

const pumpAgeOptions = ["0-5 Years", "6-10 Years", "11-15 Years", "15+ Years"];

// Map age options to conditions
const mapAgeToCondition = (age: string): string => {
  switch (age) {
    case "0-5 Years": return "New";
    case "6-10 Years": return "Good";
    case "11-15 Years": return "Average";
    case "15+ Years": return "Fair";
    default: return "Average";
  }
};

export default function OperationalMetrics({ data, onUpdate, errors }: OperationalMetricsProps) {
  // Set default pump age if not set
  React.useEffect(() => {
    if (!data.pumpAge) {
      const defaultAge = '11-15 Years';
      const defaultCondition = 'Average';
      
      onUpdate({
        pumpAge: defaultAge,
        assets: {
          ...data.assets,
          Fueling_Equipment: {
            condition: defaultCondition,
            size: data.numberOfPumps || 0
          }
        }
      });
    }
  }, []); // Run only once on component mount

  return (
    <div className="space-y-6">
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-700">
          Tell us how the station operates — hours, pumps, fuel types, and supply agreements. These factors influence equipment valuation and can significantly affect the property's market premium.
        </p>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Operating Hours
          </label>
          <Tooltip content="Select the typical operating hours of the station">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <select
          value={data.operatingHours}
          onChange={(e) => onUpdate({ operatingHours: e.target.value as '24/7' | 'specific' | 'not-applicable' })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="24/7">24/7 Operation</option>
          <option value="specific">Specific Hours</option>
          <option value="not-applicable">Not Applicable</option>
        </select>

        {data.operatingHours === 'specific' && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Specify Hours
              </label>
              <Tooltip content="Enter the typical operating hours (e.g., 6AM-10PM)">
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
            <input
              type="text"
              value={data.specificHours || ''}
              onChange={(e) => onUpdate({ specificHours: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 6AM-10PM"
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Number of Fuel Pumps
            </label>
            <Tooltip content="Total number of fueling positions">
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </div>
          <input
            type="number"
            value={data.numberOfPumps || ''}
            onChange={(e) => {
              const numPumps = Number(e.target.value);
              onUpdate({
                numberOfPumps: numPumps,
                assets: {
                  ...data.assets,
                  Fueling_Equipment: {
                    condition: data.assets?.Fueling_Equipment?.condition || 'Average',
                    size: numPumps
                  }
                }
              });
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., 8"
          />
        </div>
        
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Fuel Pump Age
            </label>
            <Tooltip content="Select the approximate age range of the fuel pumps">
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {pumpAgeOptions.map((age) => (
              <button
                key={age}
                onClick={() => {
                  const condition = mapAgeToCondition(age);
                  onUpdate({
                    pumpAge: age,
                    assets: {
                      ...data.assets,
                      Fueling_Equipment: {
                        condition: condition,
                        size: data.numberOfPumps || 0
                      }
                    }
                  });
                }}
                className={clsx(
                  "px-3 py-2 border rounded text-sm font-medium",
                  data.pumpAge === age
                    ? "bg-fc-orange-500 text-white border-fc-orange-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                )}
              >
                {age}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Years Remaining on Fuel Supply Agreement
          </label>
          <Tooltip content="Enter the number of years remaining on the current fuel supply agreement">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        
        {/* Checkbox for no agreement */}
        <div className="mb-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.noFuelAgreement || false}
              onChange={(e) => {
                const noAgreement = e.target.checked;
                onUpdate({ 
                  noFuelAgreement: noAgreement,
                  fuelSupplyYears: noAgreement ? 0 : data.fuelSupplyYears
                });
              }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">No agreement in place</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Independent/unbranded operators without formal fuel supply contracts
          </p>
        </div>

        <input
          type="number"
          value={data.noFuelAgreement ? 0 : (data.fuelSupplyYears || '')}
          onChange={(e) => onUpdate({ fuelSupplyYears: Number(e.target.value) })}
          disabled={data.noFuelAgreement}
          className={clsx(
            "w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            data.noFuelAgreement 
              ? "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200" 
              : "border-gray-300"
          )}
          placeholder="e.g., 5"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Fuel Types Available
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Tooltip content="Select all fuel types offered at the station">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        {(!data.fuelTypes || data.fuelTypes.length === 0) && (
          <div className="mb-2 text-red-500 text-sm">
            Please select at least one fuel type to continue
          </div>
        )}
        <div className="space-y-2">
          {fuelTypes.map((type) => (
            <label key={type} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.fuelTypes.includes(type)}
                onChange={(e) => {
                  const newTypes = e.target.checked
                    ? [...data.fuelTypes, type]
                    : data.fuelTypes.filter((t: string) => t !== type);
                  onUpdate({ fuelTypes: newTypes });
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{type}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}